import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  apiErrorText,
  apiLoadErrorText,
  useCollectEconBdrPeriodMutation,
  useDecideEconBdrAnomalyMutation,
  useGetEconBdrPeriodQuery,
  useGetEconBdrStatusQuery,
  useListEconBdrPeriodsQuery,
  useListPtoProjectsQuery,
  useTransitionEconBdrPeriodMutation,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { SkeletonLine } from '@shared/ui/Skeleton';
import { CheckLabel, Control, FleetChip, Notice, TextButton, typo } from '@entities/fleet';
import {
  BDR_SEVERITY_LABEL,
  currentMonth,
  formatBdrValue,
  formatRub2,
  type BdrColumn,
  type BdrLine,
} from '@entities/economic/bdrV2';
import { BdrTable } from './BdrTable';
import { LineDrawer } from './LineDrawer';
import {
  AnomalyList,
  Lead,
  MonthChip,
  MonthStrip,
  Root,
  ScopeSelect,
  Section,
  Summary,
  Toolbar,
} from './styles';

const MONTHS = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

const ARTICLE_LABEL: Readonly<Record<string, string>> = {
  materials_project: 'Согласно проектной документации',
  materials_other: 'Прочие материалы',
};

const STATUS_TONE: Readonly<Record<string, 'muted' | 'info' | 'warning' | 'success'>> = {
  draft: 'muted',
  preliminary: 'info',
  submitted: 'warning',
  approved: 'success',
};

/**
 * БДР v2: месяц × объект, собранный из первичных источников (КС, Хронум,
 * ПРЦ, приход ТМЦ, банк, план). Месяц и область — в адресе (`?month=&scope=`).
 * Экономист собирает период, правит строки с причиной, разбирает аномалии и
 * отправляет на проверку; главный экономист утверждает. При выключенном
 * флаге `OSNOVA_ECON_V2_ENABLED` экран объясняет, что БДР v2 ещё не включён.
 */
export function BdrPage() {
  const [params, setParams] = useSearchParams();
  const month = params.get('month') ?? currentMonth();
  const scope = params.get('scope') ?? 'all';
  const year = month.slice(0, 4);
  const { data: status, isLoading: statusLoading, error: statusError } = useGetEconBdrStatusQuery();
  const enabled = status?.enabled === true;
  const { data: grid } = useListEconBdrPeriodsQuery(
    { from: `${year}-01`, to: `${year}-12`, scope },
    { skip: !enabled },
  );
  const current = useMemo(
    () => grid?.items.find((p) => p.period === month && p.is_current) ?? null,
    [grid, month],
  );
  const busy = current?.collect_state === 'queued' || current?.collect_state === 'running';
  const {
    data: view,
    isLoading: viewLoading,
    error: viewError,
  } = useGetEconBdrPeriodQuery(current?.id ?? '', {
    skip: !enabled || !current,
    pollingInterval: busy ? 3000 : 0,
  });
  const { data: projects } = useListPtoProjectsQuery(undefined, { skip: !enabled });
  const [collect, collectState] = useCollectEconBdrPeriodMutation();
  const [transition, transitionState] = useTransitionEconBdrPeriodMutation();
  const [decide] = useDecideEconBdrAnomalyMutation();
  const [comment, setComment] = useState('');
  const [collapsedAll, setCollapsedAll] = useState(false);
  const [opened, setOpened] = useState<{ line: BdrLine; column: BdrColumn } | null>(null);
  const [flash, setFlash] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  function setParam(patch: Record<string, string | null>) {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          if (v === null || v === '') next.delete(k);
          else next.set(k, v);
        }
        return next;
      },
      { replace: true },
    );
  }

  async function onCollect() {
    setFlash(null);
    try {
      await collect({ month, scope: scope === 'all' ? undefined : scope }).unwrap();
      setFlash({
        tone: 'success',
        text: 'Сбор начат: источники читаются, таблица обновится сама.',
      });
    } catch (err) {
      setFlash({ tone: 'danger', text: apiErrorText(err, 'Сбор не начат.') });
    }
  }

  async function onTransition(action: 'submit' | 'approve' | 'reopen') {
    if (!view) return;
    if (action === 'reopen' && comment.trim() === '') {
      setFlash({
        tone: 'danger',
        text: 'Чтобы переоткрыть месяц, напишите причину в поле комментария.',
      });
      return;
    }
    setFlash(null);
    try {
      await transition({
        id: view.period.id,
        action,
        comment: comment.trim() || undefined,
      }).unwrap();
      setComment('');
      setFlash({
        tone: 'success',
        text:
          action === 'submit'
            ? 'Период отправлен на проверку.'
            : action === 'approve'
              ? 'Период утверждён и опубликован для руководителя.'
              : 'Месяц переоткрыт — новая версия в черновике.',
      });
    } catch (err) {
      setFlash({ tone: 'danger', text: apiErrorText(err, 'Не получилось.') });
    }
  }

  if (statusLoading) {
    return (
      <Root>
        <SkeletonLine $height={40} />
      </Root>
    );
  }
  if (statusError !== undefined && !status) {
    return (
      <Root>
        <Notice $tone="danger" role="alert">
          <span className="text">
            {apiLoadErrorText(statusError, 'БДР не загрузился.', 'Нет доступа к Экономисту.')}
          </span>
        </Notice>
      </Root>
    );
  }
  if (!enabled) {
    return (
      <Root>
        <Lead>
          {typo(
            'БДР по объектам собирается сам из первичных источников: выручка — КС, ФОТ и часы — Хронум, техника — по ПРЦ, материалы — приход ТМЦ, банк — «Деньги». Он ещё не включён: сначала сверяем проверочный месяц с Excel главного экономиста.',
          )}
        </Lead>
        <Notice $tone="info" role="note">
          <span className="text">
            {typo(
              `БДР v2 выключен (переменная ${status?.flag ?? 'OSNOVA_ECON_V2_ENABLED'}). Включает администратор после сверки.`,
            )}
          </span>
        </Notice>
      </Root>
    );
  }

  const checks = view?.checks;
  const drift = view?.materials_drift ?? [];
  const openAnomalies = (view?.anomalies ?? []).filter((a) => !a.decision);

  return (
    <Root>
      <Lead>
        {typo(
          'Месяц по объектам из первичных источников. Ячейка открывает строку: откуда сумма, документы и ручная правка с причиной. «Свернуть до разделов» — укрупнённый вид, как заголовки блоков БДР.',
        )}
      </Lead>

      {flash && (
        <Notice $tone={flash.tone} role={flash.tone === 'danger' ? 'alert' : 'status'}>
          <span className="text">{typo(flash.text)}</span>
          <TextButton type="button" onClick={() => setFlash(null)}>
            Скрыть
          </TextButton>
        </Notice>
      )}

      <Toolbar>
        <Control
          type="month"
          aria-label="Месяц"
          value={month}
          max={currentMonth()}
          onChange={(e) => setParam({ month: e.target.value || null })}
        />
        <ScopeSelect
          aria-label="Область"
          value={scope}
          onChange={(e) => setParam({ scope: e.target.value === 'all' ? null : e.target.value })}
        >
          <option value="all">Все объекты и подразделения</option>
          {(projects?.items ?? []).map((p) => (
            <option key={p.id} value={`project:${p.id}`}>
              {p.code && p.code !== p.name ? `${p.name} (${p.code})` : p.name}
            </option>
          ))}
        </ScopeSelect>
        <CheckLabel>
          <input
            type="checkbox"
            checked={collapsedAll}
            onChange={(e) => setCollapsedAll(e.target.checked)}
          />
          Свернуть до разделов
        </CheckLabel>
        <span className="spacer" />
        {(status?.permissions.write ?? false) && (
          <Button
            type="button"
            $variant="primary"
            disabled={
              collectState.isLoading ||
              busy ||
              (view !== undefined && checks?.can_collect === false)
            }
            title={checks?.collect_blocked_reason ?? undefined}
            onClick={() => void onCollect()}
          >
            {busy ? 'Собирается…' : current ? 'Собрать заново' : 'Собрать'}
          </Button>
        )}
      </Toolbar>

      <MonthStrip aria-label="Месяцы года">
        {MONTHS.map((m, i) => {
          const key = `${year}-${String(i + 1).padStart(2, '0')}`;
          const p = grid?.items.find((x) => x.period === key && x.is_current);
          return (
            <MonthChip
              key={key}
              type="button"
              $active={key === month}
              aria-pressed={key === month}
              onClick={() => setParam({ month: key })}
            >
              <span className="m">
                {m} {year}
              </span>
              <span className="s">{p ? p.status_title : 'не собран'}</span>
            </MonthChip>
          );
        })}
      </MonthStrip>

      {!current ? (
        <Notice $tone="info" role="note">
          <span className="text">{typo('Этот месяц ещё не собирали — нажмите «Собрать».')}</span>
        </Notice>
      ) : viewLoading || !view ? (
        viewError !== undefined ? (
          <Notice $tone="danger" role="alert">
            <span className="text">
              {apiLoadErrorText(viewError, 'Период не загрузился.', 'Нет доступа.')}
            </span>
          </Notice>
        ) : (
          <SkeletonLine $height={240} />
        )
      ) : (
        <>
          <Summary>
            <span>
              <FleetChip $tone={STATUS_TONE[view.period.status] ?? 'muted'}>
                {view.period.status_title}
              </FleetChip>{' '}
              версия {view.period.version}
            </span>
            {current.revenue && (
              <span>
                Доходы: <b>{formatBdrValue(current.revenue, 'rub')}</b> ₽
              </span>
            )}
            {current.costs && (
              <span>
                Расходы: <b>{formatBdrValue(current.costs, 'rub')}</b> ₽
              </span>
            )}
            {current.margin && (
              <span>
                Маржа: <b>{formatBdrValue(current.margin, 'rub')}</b> ₽
              </span>
            )}
            <span>
              «Нет данных»: <b>{view.checks.no_data_lines}</b>
            </span>
            <span>
              Критичных аномалий: <b>{view.anomaly_counts.open_critical}</b>
            </span>
          </Summary>

          {view.period.collect_state === 'failed' && view.period.collect_error && (
            <Notice $tone="danger" role="alert">
              <span className="text">{typo(`Сбор не удался: ${view.period.collect_error}`)}</span>
            </Notice>
          )}

          {drift.length > 0 && (
            <Notice $tone="warning" role="note">
              <span className="text">
                {typo(
                  `Приход ТМЦ изменился после сборки (документы внесли задним числом или исправили): ${drift
                    .map(
                      (d) =>
                        `${d.column_label ?? 'новая колонка'}, ${ARTICLE_LABEL[d.article] ?? d.article}: было ${formatRub2(d.stored)}, стало ${formatRub2(d.current)}`,
                    )
                    .join(
                      '; ',
                    )}. ${view.period.status === 'approved' ? 'Переоткройте месяц, чтобы учесть.' : 'Соберите период заново.'}`,
                )}
              </span>
            </Notice>
          )}

          <BdrTable
            view={view}
            collapsedAll={collapsedAll}
            onOpen={(line, column) => setOpened({ line, column })}
          />

          <Section aria-labelledby="bdr-actions">
            <h3 id="bdr-actions">Проверка и утверждение</h3>
            <Toolbar>
              <Control
                aria-label="Комментарий к переходу"
                placeholder="Комментарий (для «Переоткрыть» — причина)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
              />
              {view.permissions.write && (
                <Button
                  type="button"
                  $variant="secondary"
                  disabled={!checks?.can_submit || transitionState.isLoading}
                  title={checks?.submit_blocked_reason ?? undefined}
                  onClick={() => void onTransition('submit')}
                >
                  На проверку
                </Button>
              )}
              {view.permissions.approve && (
                <>
                  <Button
                    type="button"
                    $variant="primary"
                    disabled={!checks?.can_approve || transitionState.isLoading}
                    title={checks?.approve_blocked_reason ?? undefined}
                    onClick={() => void onTransition('approve')}
                  >
                    Утвердить
                  </Button>
                  <Button
                    type="button"
                    $variant="ghost"
                    disabled={!checks?.can_reopen || transitionState.isLoading}
                    title={checks?.reopen_blocked_reason ?? undefined}
                    onClick={() => void onTransition('reopen')}
                  >
                    Переоткрыть
                  </Button>
                </>
              )}
            </Toolbar>
            {(checks?.submit_blocked_reason || checks?.approve_blocked_reason) && (
              <Notice $tone="info" role="note">
                <span className="text">
                  {typo(checks?.submit_blocked_reason ?? checks?.approve_blocked_reason ?? '')}
                </span>
              </Notice>
            )}
          </Section>

          {view.anomalies.length > 0 && (
            <Section aria-labelledby="bdr-anomalies">
              <h3 id="bdr-anomalies">
                Аномалии: {view.anomalies.length}, неразобранных {openAnomalies.length}
              </h3>
              <AnomalyList>
                {view.anomalies.map((a) => (
                  <li key={a.id}>
                    <div className="head">
                      <FleetChip
                        $tone={
                          a.severity === 'critical'
                            ? 'danger'
                            : a.severity === 'major'
                              ? 'warning'
                              : 'muted'
                        }
                      >
                        {BDR_SEVERITY_LABEL[a.severity] ?? a.severity}
                      </FleetChip>
                      {a.title}
                      {a.amount && <span>· {formatRub2(a.amount)}</span>}
                      {a.decision && (
                        <FleetChip $tone="muted">
                          {a.decision === 'accepted' ? 'принято' : 'отклонено'}
                          {a.decision_by_name ? ` · ${a.decision_by_name}` : ''}
                        </FleetChip>
                      )}
                    </div>
                    <div className="desc">{typo(a.description)}</div>
                    {view.permissions.write && (
                      <div className="actions">
                        <TextButton
                          type="button"
                          onClick={() => void decide({ id: a.id, decision: 'accepted' })}
                        >
                          Принять
                        </TextButton>
                        <TextButton
                          type="button"
                          onClick={() => void decide({ id: a.id, decision: 'rejected' })}
                        >
                          Отклонить
                        </TextButton>
                        {a.decision && (
                          <TextButton
                            type="button"
                            onClick={() => void decide({ id: a.id, decision: null })}
                          >
                            Снять решение
                          </TextButton>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </AnomalyList>
            </Section>
          )}
        </>
      )}

      {opened && view && (
        <LineDrawer
          key={opened.line.id}
          line={opened.line}
          column={opened.column}
          article={view.articles.find((a) => a.code === opened.line.article_code)}
          canEdit={view.checks.can_edit}
          onClose={() => setOpened(null)}
          onSaved={(text) => {
            setOpened(null);
            setFlash({ tone: 'success', text });
          }}
        />
      )}
    </Root>
  );
}
